import type { LucideIcon } from 'lucide-react'
import {
  LayoutDashboard,
  Database,
  ShoppingCart,
  Boxes,
  Factory,
  Send,
  ShoppingBag,
  Wallet,
  BarChart3,
  UserCog,
  Settings,
  ListChecks,
} from 'lucide-react'

export type ModuleId =
  | 'dashboard'
  | 'master-data'
  | 'sales'
  | 'inventory'
  | 'production'
  | 'dispatch'
  | 'purchases'
  | 'finance'
  | 'tasks'
  | 'reports'
  | 'user-management'
  | 'settings'

export interface ModuleTheme {
  id: ModuleId
  label: string
  /** Icon container on dark sidebar */
  sidebarIconBg: string
  sidebarIconText: string
  sidebarActiveBg: string
  sidebarActiveText: string
  sidebarIndicator: string
  /** Icon container on light surfaces */
  iconBg: string
  iconText: string
  accent: string
  accentText: string
  accentBorder: string
  accentMuted: string
  /** KPI / card gradient accent */
  cardGradient: string
  cardGlow: string
  /** Data table column header band */
  tableHeadBg: string
  tableHeadText: string
  /** Table row hover (full Tailwind string — must live here for purge) */
  rowHover: string
  /** Solid banner header (accent pages) */
  headerSurface: string
  headerTitle: string
  headerMuted: string
  headerLabel: string
  headerIcon: string
}

export const MODULE_THEMES: Record<ModuleId, ModuleTheme> = {
  dashboard: {
    id: 'dashboard',
    label: 'Dashboard',
    sidebarIconBg: 'bg-blue-600',
    sidebarIconText: 'text-white',
    sidebarActiveBg: 'bg-blue-500/16',
    sidebarActiveText: 'text-white',
    sidebarIndicator: 'bg-blue-500',
    iconBg: 'bg-blue-50 dark:bg-blue-950/40',
    iconText: 'text-blue-600 dark:text-blue-400',
    accent: 'bg-blue-500',
    accentText: 'text-blue-600 dark:text-blue-400',
    accentBorder: 'border-blue-500/30',
    accentMuted: 'bg-blue-500/8',
    cardGradient: 'from-blue-500/[0.07] via-transparent to-transparent',
    cardGlow: 'shadow-[0_0_0_1px_rgba(59,130,246,0.08),0_4px_16px_-4px_rgba(59,130,246,0.12)]',
    tableHeadBg:
      'bg-gradient-to-r from-blue-200/95 via-blue-100 to-sky-50 dark:from-blue-950/80 dark:via-blue-950/55 dark:to-slate-950/40',
    tableHeadText: 'text-blue-950 dark:text-blue-50',
    rowHover:
      'hover:bg-blue-500/[0.05] focus-visible:bg-blue-500/[0.06] hover:shadow-[inset_3px_0_0_0_rgb(59_130_246/0.5)]',
    headerSurface: 'bg-gradient-to-br from-blue-800 via-blue-600 to-indigo-900',
    headerTitle: 'text-white',
    headerMuted: 'text-blue-100',
    headerLabel: 'text-blue-200',
    headerIcon: 'bg-white/15 text-white ring-1 ring-white/25 shadow-lg backdrop-blur-sm',
  },
  'master-data': {
    id: 'master-data',
    label: 'Database',
    sidebarIconBg: 'bg-teal-600',
    sidebarIconText: 'text-white',
    sidebarActiveBg: 'bg-teal-500/16',
    sidebarActiveText: 'text-white',
    sidebarIndicator: 'bg-teal-400',
    iconBg: 'bg-teal-50 dark:bg-teal-950/40',
    iconText: 'text-teal-700 dark:text-teal-300',
    accent: 'bg-teal-600',
    accentText: 'text-teal-700 dark:text-teal-300',
    accentBorder: 'border-teal-500/25',
    accentMuted: 'bg-teal-500/8',
    cardGradient: 'from-teal-500/[0.08] via-transparent to-transparent',
    cardGlow: 'shadow-[0_0_0_1px_rgba(20,184,166,0.08),0_4px_16px_-4px_rgba(20,184,166,0.12)]',
    tableHeadBg:
      'bg-gradient-to-r from-teal-200/95 via-teal-100 to-cyan-50 dark:from-teal-950/80 dark:via-teal-950/55 dark:to-slate-950/40',
    tableHeadText: 'text-teal-950 dark:text-teal-50',
    rowHover:
      'hover:bg-teal-500/[0.06] focus-visible:bg-teal-500/[0.07] hover:shadow-[inset_3px_0_0_0_rgb(20_184_166/0.55)]',
    headerSurface: 'bg-gradient-to-br from-teal-800 via-teal-600 to-cyan-900',
    headerTitle: 'text-white',
    headerMuted: 'text-teal-100',
    headerLabel: 'text-teal-200',
    headerIcon: 'bg-white/15 text-white ring-1 ring-white/25 shadow-lg backdrop-blur-sm',
  },
  sales: {
    id: 'sales',
    label: 'Sales',
    sidebarIconBg: 'bg-emerald-600',
    sidebarIconText: 'text-white',
    sidebarActiveBg: 'bg-emerald-500/16',
    sidebarActiveText: 'text-white',
    sidebarIndicator: 'bg-emerald-500',
    iconBg: 'bg-emerald-50 dark:bg-emerald-950/40',
    iconText: 'text-emerald-600 dark:text-emerald-400',
    accent: 'bg-emerald-500',
    accentText: 'text-emerald-600 dark:text-emerald-400',
    accentBorder: 'border-emerald-500/30',
    accentMuted: 'bg-emerald-500/8',
    cardGradient: 'from-emerald-500/[0.07] via-transparent to-transparent',
    cardGlow: 'shadow-[0_0_0_1px_rgba(16,185,129,0.08),0_4px_16px_-4px_rgba(16,185,129,0.12)]',
    tableHeadBg:
      'bg-gradient-to-r from-emerald-200/95 via-emerald-100 to-teal-50 dark:from-emerald-950/80 dark:via-emerald-950/55 dark:to-slate-950/40',
    tableHeadText: 'text-emerald-950 dark:text-emerald-50',
    rowHover:
      'hover:bg-emerald-500/[0.05] focus-visible:bg-emerald-500/[0.06] hover:shadow-[inset_3px_0_0_0_rgb(16_185_129/0.5)]',
    headerSurface: 'bg-gradient-to-br from-emerald-800 via-emerald-600 to-teal-900',
    headerTitle: 'text-white',
    headerMuted: 'text-emerald-100',
    headerLabel: 'text-emerald-200',
    headerIcon: 'bg-white/15 text-white ring-1 ring-white/25 shadow-lg backdrop-blur-sm',
  },
  inventory: {
    id: 'inventory',
    label: 'Inventory',
    sidebarIconBg: 'bg-amber-600',
    sidebarIconText: 'text-white',
    sidebarActiveBg: 'bg-amber-500/16',
    sidebarActiveText: 'text-white',
    sidebarIndicator: 'bg-amber-500',
    iconBg: 'bg-amber-50 dark:bg-amber-950/40',
    iconText: 'text-amber-600 dark:text-amber-400',
    accent: 'bg-amber-500',
    accentText: 'text-amber-600 dark:text-amber-400',
    accentBorder: 'border-amber-500/30',
    accentMuted: 'bg-amber-500/8',
    cardGradient: 'from-amber-500/[0.07] via-transparent to-transparent',
    cardGlow: 'shadow-[0_0_0_1px_rgba(245,158,11,0.08),0_4px_16px_-4px_rgba(245,158,11,0.12)]',
    tableHeadBg:
      'bg-gradient-to-r from-amber-200/95 via-amber-100 to-orange-50 dark:from-amber-950/80 dark:via-amber-950/55 dark:to-slate-950/40',
    tableHeadText: 'text-amber-950 dark:text-amber-50',
    rowHover:
      'hover:bg-amber-500/[0.05] focus-visible:bg-amber-500/[0.06] hover:shadow-[inset_3px_0_0_0_rgb(245_158_11/0.5)]',
    headerSurface: 'bg-gradient-to-br from-amber-800 via-amber-600 to-orange-900',
    headerTitle: 'text-white',
    headerMuted: 'text-amber-100',
    headerLabel: 'text-amber-200',
    headerIcon: 'bg-white/15 text-white ring-1 ring-white/25 shadow-lg backdrop-blur-sm',
  },
  production: {
    id: 'production',
    label: 'Production',
    sidebarIconBg: 'bg-orange-600',
    sidebarIconText: 'text-white',
    sidebarActiveBg: 'bg-orange-500/16',
    sidebarActiveText: 'text-white',
    sidebarIndicator: 'bg-orange-500',
    iconBg: 'bg-orange-50 dark:bg-orange-950/40',
    iconText: 'text-orange-600 dark:text-orange-400',
    accent: 'bg-orange-500',
    accentText: 'text-orange-600 dark:text-orange-400',
    accentBorder: 'border-orange-500/30',
    accentMuted: 'bg-orange-500/8',
    cardGradient: 'from-orange-500/[0.07] via-transparent to-transparent',
    cardGlow: 'shadow-[0_0_0_1px_rgba(249,115,22,0.08),0_4px_16px_-4px_rgba(249,115,22,0.12)]',
    tableHeadBg:
      'bg-gradient-to-r from-orange-200/95 via-orange-100 to-amber-50 dark:from-orange-950/80 dark:via-orange-950/55 dark:to-slate-950/40',
    tableHeadText: 'text-orange-950 dark:text-orange-50',
    rowHover:
      'hover:bg-orange-500/[0.05] focus-visible:bg-orange-500/[0.06] hover:shadow-[inset_3px_0_0_0_rgb(249_115_22/0.5)]',
    headerSurface: 'bg-gradient-to-br from-orange-800 via-orange-600 to-red-900',
    headerTitle: 'text-white',
    headerMuted: 'text-orange-100',
    headerLabel: 'text-orange-200',
    headerIcon: 'bg-white/15 text-white ring-1 ring-white/25 shadow-lg backdrop-blur-sm',
  },
  dispatch: {
    id: 'dispatch',
    label: 'Dispatch',
    sidebarIconBg: 'bg-cyan-600',
    sidebarIconText: 'text-white',
    sidebarActiveBg: 'bg-cyan-500/16',
    sidebarActiveText: 'text-white',
    sidebarIndicator: 'bg-cyan-500',
    iconBg: 'bg-cyan-50 dark:bg-cyan-950/40',
    iconText: 'text-cyan-600 dark:text-cyan-400',
    accent: 'bg-cyan-500',
    accentText: 'text-cyan-600 dark:text-cyan-400',
    accentBorder: 'border-cyan-500/30',
    accentMuted: 'bg-cyan-500/8',
    cardGradient: 'from-cyan-500/[0.07] via-transparent to-transparent',
    cardGlow: 'shadow-[0_0_0_1px_rgba(6,182,212,0.08),0_4px_16px_-4px_rgba(6,182,212,0.12)]',
    tableHeadBg:
      'bg-gradient-to-r from-cyan-200/95 via-cyan-100 to-sky-50 dark:from-cyan-950/80 dark:via-cyan-950/55 dark:to-slate-950/40',
    tableHeadText: 'text-cyan-950 dark:text-cyan-50',
    rowHover:
      'hover:bg-cyan-500/[0.06] focus-visible:bg-cyan-500/[0.07] hover:shadow-[inset_3px_0_0_0_rgb(6_182_212/0.55)]',
    headerSurface: 'bg-gradient-to-br from-cyan-800 via-cyan-600 to-sky-900',
    headerTitle: 'text-white',
    headerMuted: 'text-cyan-100',
    headerLabel: 'text-cyan-200',
    headerIcon: 'bg-white/15 text-white ring-1 ring-white/25 shadow-lg backdrop-blur-sm',
  },
  purchases: {
    id: 'purchases',
    label: 'Purchases',
    sidebarIconBg: 'bg-violet-600',
    sidebarIconText: 'text-white',
    sidebarActiveBg: 'bg-violet-500/16',
    sidebarActiveText: 'text-white',
    sidebarIndicator: 'bg-violet-500',
    iconBg: 'bg-violet-50 dark:bg-violet-950/40',
    iconText: 'text-violet-700 dark:text-violet-300',
    accent: 'bg-violet-600',
    accentText: 'text-violet-700 dark:text-violet-300',
    accentBorder: 'border-violet-500/25',
    accentMuted: 'bg-violet-500/8',
    cardGradient: 'from-violet-500/[0.08] via-transparent to-transparent',
    cardGlow: 'shadow-[0_0_0_1px_rgba(139,92,246,0.08),0_4px_16px_-4px_rgba(139,92,246,0.12)]',
    tableHeadBg:
      'bg-gradient-to-r from-violet-200/95 via-violet-100 to-fuchsia-50 dark:from-violet-950/80 dark:via-violet-950/55 dark:to-slate-950/40',
    tableHeadText: 'text-violet-950 dark:text-violet-50',
    rowHover:
      'hover:bg-violet-500/[0.06] focus-visible:bg-violet-500/[0.07] hover:shadow-[inset_3px_0_0_0_rgb(139_92_246/0.55)]',
    headerSurface: 'bg-gradient-to-br from-violet-800 via-violet-600 to-indigo-900',
    headerTitle: 'text-white',
    headerMuted: 'text-violet-100',
    headerLabel: 'text-violet-200',
    headerIcon: 'bg-white/15 text-white ring-1 ring-white/25 shadow-lg backdrop-blur-sm',
  },
  finance: {
    id: 'finance',
    label: 'Finance',
    sidebarIconBg: 'bg-rose-600',
    sidebarIconText: 'text-white',
    sidebarActiveBg: 'bg-rose-500/16',
    sidebarActiveText: 'text-white',
    sidebarIndicator: 'bg-rose-500',
    iconBg: 'bg-rose-50 dark:bg-rose-950/40',
    iconText: 'text-rose-600 dark:text-rose-400',
    accent: 'bg-rose-500',
    accentText: 'text-rose-600 dark:text-rose-400',
    accentBorder: 'border-rose-500/30',
    accentMuted: 'bg-rose-500/8',
    cardGradient: 'from-rose-500/[0.07] via-transparent to-transparent',
    cardGlow: 'shadow-[0_0_0_1px_rgba(244,63,94,0.08),0_4px_16px_-4px_rgba(244,63,94,0.12)]',
    tableHeadBg:
      'bg-gradient-to-r from-rose-200/95 via-rose-100 to-pink-50 dark:from-rose-950/80 dark:via-rose-950/55 dark:to-slate-950/40',
    tableHeadText: 'text-rose-950 dark:text-rose-50',
    rowHover:
      'hover:bg-rose-500/[0.05] focus-visible:bg-rose-500/[0.06] hover:shadow-[inset_3px_0_0_0_rgb(244_63_94/0.5)]',
    headerSurface: 'bg-gradient-to-br from-rose-800 via-rose-600 to-pink-900',
    headerTitle: 'text-white',
    headerMuted: 'text-rose-100',
    headerLabel: 'text-rose-200',
    headerIcon: 'bg-white/15 text-white ring-1 ring-white/25 shadow-lg backdrop-blur-sm',
  },
  tasks: {
    id: 'tasks',
    label: 'Tasks',
    sidebarIconBg: 'bg-sky-600',
    sidebarIconText: 'text-white',
    sidebarActiveBg: 'bg-sky-500/16',
    sidebarActiveText: 'text-white',
    sidebarIndicator: 'bg-sky-500',
    iconBg: 'bg-sky-50 dark:bg-sky-950/40',
    iconText: 'text-sky-700 dark:text-sky-400',
    accent: 'bg-sky-600',
    accentText: 'text-sky-700 dark:text-sky-400',
    accentBorder: 'border-sky-500/25',
    accentMuted: 'bg-sky-500/8',
    cardGradient: 'from-sky-500/[0.07] via-transparent to-transparent',
    cardGlow: 'shadow-[0_0_0_1px_rgba(2,132,199,0.08),0_4px_16px_-4px_rgba(2,132,199,0.12)]',
    tableHeadBg:
      'bg-gradient-to-r from-sky-200/95 via-sky-100 to-blue-50 dark:from-sky-950/80 dark:via-sky-950/55 dark:to-slate-950/40',
    tableHeadText: 'text-sky-950 dark:text-sky-50',
    rowHover:
      'hover:bg-sky-500/[0.05] focus-visible:bg-sky-500/[0.06] hover:shadow-[inset_3px_0_0_0_rgb(2_132_199/0.5)]',
    headerSurface: 'bg-gradient-to-br from-sky-800 via-sky-600 to-blue-900',
    headerTitle: 'text-white',
    headerMuted: 'text-sky-100',
    headerLabel: 'text-sky-200',
    headerIcon: 'bg-white/15 text-white ring-1 ring-white/25 shadow-lg backdrop-blur-sm',
  },
  reports: {
    id: 'reports',
    label: 'Reports',
    sidebarIconBg: 'bg-indigo-600',
    sidebarIconText: 'text-white',
    sidebarActiveBg: 'bg-indigo-500/16',
    sidebarActiveText: 'text-white',
    sidebarIndicator: 'bg-indigo-500',
    iconBg: 'bg-indigo-50 dark:bg-indigo-950/40',
    iconText: 'text-indigo-600 dark:text-indigo-400',
    accent: 'bg-indigo-500',
    accentText: 'text-indigo-600 dark:text-indigo-400',
    accentBorder: 'border-indigo-500/30',
    accentMuted: 'bg-indigo-500/8',
    cardGradient: 'from-indigo-500/[0.07] via-transparent to-transparent',
    cardGlow: 'shadow-[0_0_0_1px_rgba(99,102,241,0.08),0_4px_16px_-4px_rgba(99,102,241,0.12)]',
    tableHeadBg:
      'bg-gradient-to-r from-indigo-200/95 via-indigo-100 to-violet-50 dark:from-indigo-950/80 dark:via-indigo-950/55 dark:to-slate-950/40',
    tableHeadText: 'text-indigo-950 dark:text-indigo-50',
    rowHover:
      'hover:bg-indigo-500/[0.05] focus-visible:bg-indigo-500/[0.06] hover:shadow-[inset_3px_0_0_0_rgb(99_102_241/0.5)]',
    headerSurface: 'bg-gradient-to-br from-indigo-800 via-indigo-600 to-blue-900',
    headerTitle: 'text-white',
    headerMuted: 'text-indigo-100',
    headerLabel: 'text-indigo-200',
    headerIcon: 'bg-white/15 text-white ring-1 ring-white/25 shadow-lg backdrop-blur-sm',
  },
  'user-management': {
    id: 'user-management',
    label: 'Users',
    sidebarIconBg: 'bg-teal-600',
    sidebarIconText: 'text-white',
    sidebarActiveBg: 'bg-teal-500/16',
    sidebarActiveText: 'text-white',
    sidebarIndicator: 'bg-teal-500',
    iconBg: 'bg-teal-50 dark:bg-teal-950/40',
    iconText: 'text-teal-600 dark:text-teal-400',
    accent: 'bg-teal-500',
    accentText: 'text-teal-600 dark:text-teal-400',
    accentBorder: 'border-teal-500/30',
    accentMuted: 'bg-teal-500/8',
    cardGradient: 'from-teal-500/[0.07] via-transparent to-transparent',
    cardGlow: 'shadow-[0_0_0_1px_rgba(20,184,166,0.08),0_4px_16px_-4px_rgba(20,184,166,0.12)]',
    tableHeadBg:
      'bg-gradient-to-r from-teal-200/95 via-teal-100 to-emerald-50 dark:from-teal-950/80 dark:via-teal-950/55 dark:to-slate-950/40',
    tableHeadText: 'text-teal-950 dark:text-teal-50',
    rowHover:
      'hover:bg-teal-500/[0.05] focus-visible:bg-teal-500/[0.06] hover:shadow-[inset_3px_0_0_0_rgb(20_184_166/0.5)]',
    headerSurface: 'bg-gradient-to-br from-teal-800 via-teal-600 to-cyan-900',
    headerTitle: 'text-white',
    headerMuted: 'text-teal-100',
    headerLabel: 'text-teal-200',
    headerIcon: 'bg-white/15 text-white ring-1 ring-white/25 shadow-lg backdrop-blur-sm',
  },
  settings: {
    id: 'settings',
    label: 'Settings',
    sidebarIconBg: 'bg-zinc-600',
    sidebarIconText: 'text-white',
    sidebarActiveBg: 'bg-zinc-500/16',
    sidebarActiveText: 'text-white',
    sidebarIndicator: 'bg-zinc-400',
    iconBg: 'bg-gray-100 dark:bg-gray-800/60',
    iconText: 'text-gray-600 dark:text-gray-400',
    accent: 'bg-gray-500',
    accentText: 'text-gray-600 dark:text-gray-400',
    accentBorder: 'border-gray-400/30',
    accentMuted: 'bg-gray-500/8',
    cardGradient: 'from-gray-500/[0.07] via-transparent to-transparent',
    cardGlow: 'shadow-[0_0_0_1px_rgba(107,114,128,0.08),0_4px_16px_-4px_rgba(107,114,128,0.1)]',
    tableHeadBg:
      'bg-gradient-to-r from-zinc-200 via-zinc-100 to-slate-50 dark:from-zinc-800/90 dark:via-zinc-800/60 dark:to-slate-950/50',
    tableHeadText: 'text-zinc-900 dark:text-zinc-50',
    rowHover:
      'hover:bg-zinc-500/[0.06] focus-visible:bg-zinc-500/[0.08] hover:shadow-[inset_3px_0_0_0_rgb(113_113_122/0.5)]',
    headerSurface: 'bg-gradient-to-br from-zinc-700 via-zinc-600 to-zinc-800',
    headerTitle: 'text-white',
    headerMuted: 'text-zinc-200',
    headerLabel: 'text-zinc-300',
    headerIcon: 'bg-white/15 text-white ring-1 ring-white/25 shadow-lg backdrop-blur-sm',
  },
}

export const MODULE_ICONS: Record<ModuleId, LucideIcon> = {
  dashboard: LayoutDashboard,
  'master-data': Database,
  sales: ShoppingCart,
  inventory: Boxes,
  production: Factory,
  dispatch: Send,
  purchases: ShoppingBag,
  finance: Wallet,
  tasks: ListChecks,
  reports: BarChart3,
  'user-management': UserCog,
  settings: Settings,
}

/** Map legacy ComingSoon module labels to module IDs */
export const MODULE_LABEL_TO_ID: Record<string, ModuleId> = {
  Dashboard: 'dashboard',
  'Master Data': 'master-data',
  Sales: 'sales',
  Inventory: 'inventory',
  Production: 'production',
  Dispatch: 'dispatch',
  Purchases: 'purchases',
  Finance: 'finance',
  Tasks: 'tasks',
  Reports: 'reports',
  Analytics: 'reports',
  Users: 'user-management',
  'User Management': 'user-management',
  System: 'settings',
  Settings: 'settings',
}

export function getModuleTheme(id: ModuleId): ModuleTheme {
  return MODULE_THEMES[id] ?? MODULE_THEMES.dashboard
}
